import React, { useEffect, useState, useMemo } from "react";
import { AdminAppShell } from "../design-system/shells/AdminAppShell";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import StatusChip from "../components/StatusChip";
import { Button } from "../components/ui/button";
import { ThumbsUp, ThumbsDown, BookOpen, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { AttachmentsPanel } from "../design-system/intake/InformationIntake";

function renderMarkdown(md) {
  // Minimal MD renderer — headings, bold, italic, lists, tables, code, hr, links, paragraphs.
  if (!md) return null;
  const escape = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  let html = escape(md);
  // Tables
  html = html.replace(/^\|(.+)\|\n\|([\s:|-]+)\|\n((?:\|.+\|\n?)+)/gm, (_, hdr, _sep, body) => {
    const headers = hdr.split("|").map((h) => h.trim()).filter(Boolean);
    const rows = body.trim().split("\n").map((r) => r.split("|").map((c) => c.trim()).filter((c, i, a) => !(i === 0 && c === "") && !(i === a.length - 1 && c === "")));
    return `<table class="erf-table w-full my-3 border border-erf-borderCream"><thead class="bg-erf-creamSecondary"><tr>${headers.map(h=>`<th class="px-3 py-2 text-left">${h}</th>`).join("")}</tr></thead><tbody>${rows.map(r=>`<tr class="border-t border-erf-borderCream/60">${r.map(c=>`<td class="px-3 py-2">${c}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
  });
  html = html
    .replace(/^### (.*)$/gm, '<h3 class="font-display font-semibold text-lg mt-6 mb-2">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 class="font-display font-bold text-xl mt-8 mb-3">$1</h2>')
    .replace(/^# (.*)$/gm, '<h1 class="font-display font-bold text-2xl mt-2 mb-4">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="font-mono text-[12px] bg-erf-creamSecondary px-1 py-0.5 rounded">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a class="text-erf-goldHover underline" href="$2">$1</a>')
    .replace(/^- (.*)$/gm, '<li class="ml-6 list-disc">$1</li>')
    .replace(/^(\d+)\. (.*)$/gm, '<li class="ml-6 list-decimal">$2</li>')
    .replace(/\n\n/g, '</p><p class="my-2">')
    .replace(/^(<li[\s\S]*?<\/li>)$/gm, '<ul>$1</ul>');
  return `<div class="prose max-w-none text-sm text-erf-dark font-nav leading-relaxed"><p class="my-2">${html}</p></div>`;
}

export default function HelpCenter() {
  const [articles, setArticles] = useState([]);
  const [current, setCurrent] = useState(null);
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role_key === "OWNER_SUPER_ADMIN";

  useEffect(() => {
    api.get("/help/articles").then((r) => {
      setArticles(r.data);
      if (r.data[0]) load(r.data[0].slug);
    });
  }, []);

  const load = async (slug) => {
    const r = await api.get(`/help/articles/${slug}`);
    setCurrent(r.data);
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const a of articles) (g[a.section || "General"] ??= []).push(a);
    return g;
  }, [articles]);

  const filtered = useMemo(() => {
    if (!q.trim()) return articles;
    const s = q.toLowerCase();
    return articles.filter((a) => (a.title || "").toLowerCase().includes(s) || (a.section || "").toLowerCase().includes(s));
  }, [articles, q]);

  const feedback = async (helpful) => {
    if (!current) return;
    await api.post("/help/feedback", { slug: current.slug, helpful });
    toast.success("Thanks for the feedback");
  };

  return (
    <AdminAppShell activeNav="help" title="Help Center & Tutorials" subtitle="Honest, phase-tagged documentation. Only what actually works today." user={user}>
      {isOwner && (
        <div className="mb-4 p-4 bg-white border border-erf-borderCream rounded-md flex items-center justify-between gap-3" data-testid="help-owner-tools">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">Owner tools</div>
            <div className="font-display font-semibold text-erf-dark mt-0.5">Product tour</div>
            <div className="text-xs font-nav text-erf-dark/60 mt-0.5">Replay the first-run walkthrough of the Owner Command Center. Progress is stored per-user; restarting resets your record.</div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/owner?tour=restart")}
            data-testid="restart-owner-tour"
            className="whitespace-nowrap"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Restart Owner Tour
          </Button>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="bg-white border border-erf-borderCream rounded-md p-3 max-h-[80vh] overflow-y-auto" data-testid="help-toc">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" data-testid="help-search" />
          {q.trim() ? (
            <ul className="space-y-1">
              {filtered.map((a) => (
                <li key={a.slug}>
                  <button className="w-full text-left px-2 py-1.5 text-sm font-nav hover:bg-erf-creamSecondary rounded" onClick={() => load(a.slug)}>
                    {a.title}
                  </button>
                </li>
              ))}
            </ul>
          ) : Object.entries(grouped).map(([section, list]) => (
            <div key={section} className="mb-4">
              <div className="px-2 py-1 text-[10px] font-nav uppercase tracking-widest text-erf-dark/50">{section}</div>
              <ul className="space-y-1">
                {list.map((a) => (
                  <li key={a.slug}>
                    <button
                      onClick={() => load(a.slug)}
                      data-testid={`help-article-${a.slug}`}
                      className={`w-full text-left px-2 py-1.5 text-sm font-nav rounded flex items-center justify-between ${current?.slug === a.slug ? "bg-erf-gold/15 text-erf-dark" : "hover:bg-erf-creamSecondary"}`}
                    >
                      <span className="truncate">{a.title}</span>
                      {a.status && a.status !== "LIVE" && (
                        <span className="mono text-[9px] px-1 py-0.5 rounded-sm bg-erf-creamSecondary text-erf-dark/60">{a.status}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </aside>

        <article className="bg-white border border-erf-borderCream rounded-md p-6" data-testid="help-article-body">
          {current ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-erf-dark/50">{current.section} · Phase {current.applies_to_phase}</div>
                  <h1 className="mt-1 font-display font-bold text-2xl text-erf-dark">{current.title}</h1>
                </div>
                <StatusChip status={current.status === "LIVE" ? "connected" : current.status === "DEMONSTRATION" ? "demonstration" : "phase_n"}
                  label={current.status} phase={current.applies_to_phase} />
              </div>
              <hr className="my-4 border-erf-borderCream" />
              <div dangerouslySetInnerHTML={{ __html: renderMarkdown(current.body_markdown) }} />
              <hr className="my-6 border-erf-borderCream" />
              <div className="flex items-center gap-3">
                <span className="text-xs font-nav text-erf-dark/60">Was this helpful?</span>
                <Button size="sm" variant="outline" onClick={() => feedback(true)} data-testid="help-thumbs-up"><ThumbsUp className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => feedback(false)} data-testid="help-thumbs-down"><ThumbsDown className="w-4 h-4" /></Button>
              </div>
              {/* 3e retrofit — attach screenshots/logs to help article feedback (viewer). */}
              <hr className="my-6 border-erf-borderCream" />
              <div className="text-[10px] font-mono uppercase text-erf-dark/50">Attached to your feedback on this article (internal)</div>
              <AttachmentsPanel recordType="help_feedback" recordId={current.slug} />
            </>
          ) : (
            <div className="text-erf-dark/60 font-mono text-sm flex items-center gap-2"><BookOpen className="w-4 h-4" /> Loading…</div>
          )}
        </article>
      </div>
    </AdminAppShell>
  );
}
